import React, { FC, Fragment, useCallback, useContext, useState } from 'react';
import { Alert, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ProcuredActionFilter } from 'models/action';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import TenderedActionItem from './tendered-action-item.component';
import TenderedActionsPageActions from './tendered-actions-page-actions.component';
import EditTenderedActionDialog from './edit-tendered-action-dialog/edit-tendered-action-dialog.component';
import { ActionPrice } from 'models/organization';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getActionBasedActionListTemplate } from 'apollo/templates/client-directory';
import Loader from 'components/shared/loader/loader.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';

const TenderedActions: FC = () => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'orders', 'lab-settings']);
    const [filters, setFilters] = useState<ProcuredActionFilter>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [actionBasedActionList, setActionBasedActionList] = useState<ActionPrice[]>([]);
    const [actionToEdit, setActionToEdit] = useState<ActionPrice>({} as ActionPrice);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [getActionBasedActions, { loading, refetch }] = useLazyQuery(getActionBasedActionListTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ actionBasedActionList }) {
            setActionBasedActionList(actionBasedActionList || []);
        },
    });

    const handleFilterChange = useCallback(
        debounce(
            ({
                clinicId,
                freetext,
                category,
                productionPlanTemplate,
                productionValueTemplate,
                active,
            }: ProcuredActionFilter) => {
                setFilters({ clinicId, freetext, category, productionPlanTemplate, productionValueTemplate, active });
                if (!clinicId) {
                    setActionBasedActionList([]);
                    return;
                }
                getActionBasedActions({
                    variables: {
                        clinicId: clinicId,
                        filters: {
                            freetext,
                            mainCategory: category,
                            productionPlanTemplate,
                            productionValueTemplate,
                            active,
                        },
                    },
                });
            },
            300,
        ),
        [],
    );

    const handleEditAction = (action: ActionPrice) => {
        setActionToEdit(action);
        setDialogOpen(true);
    };

    const handleUpdateAction = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('lab-settings:ACTION_UPDATED'),
        });
        refetch();
    };

    return (
        <Fragment>
            <Box mb={1}>
                <TenderedActionsPageActions
                    actionBasedActionList={actionBasedActionList}
                    onFilterChange={handleFilterChange}
                />
            </Box>

            <Fragment>
                {loading ? (
                    <Loader />
                ) : (
                    <Fragment>
                        {!actionBasedActionList.length ? (
                            filters?.clinicId && (
                                <Box mt={2}>
                                    <Alert severity="info">
                                        {filters?.active && translate('lab-settings:NO_ACTIVE_ACTIONS')}
                                        {filters?.active === false && translate('lab-settings:NO_INACTIVE_ACTIONS')}
                                        {filters?.active === null && translate('lab-settings:NO_ACTIONS')}
                                    </Alert>
                                </Box>
                            )
                        ) : (
                            <Paper variant="outlined">
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{translate('actions-materials:CODE')}</TableCell>
                                                <TableCell>{translate('orders:ACTION')}</TableCell>
                                                <TableCell>{translate('lab-settings:MAIN_CATEGORY')}</TableCell>
                                                <TableCell>{translate('lab-settings:SUB_CATEGORY')}</TableCell>
                                                <TableCell>{translate('lab-settings:MATERIAL_MACROS')}</TableCell>
                                                <TableCell>{translate('actions-materials:PRODUCTION_PLAN')}</TableCell>
                                                <TableCell>{translate('actions-materials:VALUE_TEMPLATE')}</TableCell>
                                                <TableCell align="center">{translate('common:STATUS')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {actionBasedActionList.map((action, index) => (
                                                <TenderedActionItem
                                                    key={index}
                                                    action={action}
                                                    onActionRowClick={handleEditAction}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}
                    </Fragment>
                )}
            </Fragment>
            {filters?.clinicId && dialogOpen && (
                <EditTenderedActionDialog
                    dialogOpen={dialogOpen}
                    customerId={filters.clinicId}
                    actionToEdit={actionToEdit}
                    onEditAction={handleUpdateAction}
                    onCloseDialog={() => setDialogOpen(false)}
                />
            )}
        </Fragment>
    );
};

export default TenderedActions;
